import { AfterViewInit, inject, Component } from '@angular/core';
import {
  EstimationGroupDto,
  EstimationGroupMethodDto,
  EstimationGroupMethodMethod,
  VeeService
} from '@app/api/dmc';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { LucideAngularModule, Plus, X } from 'lucide-angular';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray, transferArrayItem
} from '@angular/cdk/drag-drop';
import { AlertComponent } from '@shared/components/alert/alert.component';
import { VeeCreateObject } from '@shared/interfaces/vee/vee-create-object.interface';
import { FormApiService } from '@app/core/services/form-api.service';
import { getVeeAvailableEstimationMethods } from '@shared/modals/vee/vee-available-estimation-methods';
import { EstimationMethod } from '@shared/modals/vee/estimation-method.enum';
import { AppConfigService } from '@app/core/config/app-config.service';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-vee-create-edit-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    InputDirective,
    LucideAngularModule,
    CdkDropList,
    CdkDrag,
    AlertComponent,
    CdkDropListGroup,
    FormsModule,
  ],
  templateUrl: './vee-create-edit-modal.component.html',
  styleUrl: './vee-create-edit-modal.component.css'
})
export class VeeCreateEditModalComponent implements AfterViewInit, HasUnsavedChanges{
  private readonly appConfig: AppConfigService = inject(AppConfigService);

  public hasUnsavedChanges(): boolean {
    return this.veeForm.dirty;
  }

  public veeForm: FormGroup = new FormGroup({
    id: new FormControl(null),
    name: new FormControl(null, [Validators.required]),
    estimateAfter: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(10000)]),
    estimationMethods: new FormControl([]),
  });

  /** VEE **/
  public id?: string;
  public vee?: EstimationGroupDto;

  /** Icons **/
  protected readonly Plus = Plus;
  protected readonly X = X;

  /** Modern Lists **/
  public activeEstimationMethods: VeeCreateObject[] = [];

  public availableEstimationMethods: VeeCreateObject[] = [];

  constructor(
    private readonly veeService: VeeService,
    private readonly formApiService: FormApiService
  ) {
    this.availableEstimationMethods = getVeeAvailableEstimationMethods(
      this.appConfig.settings.featureFlags.machineLearningEstimation.enabled
    );
  }

  public ngAfterViewInit(): void {
    if (this.id) {
      this.veeService.veeGetByIdGet(this.id).subscribe((data: EstimationGroupDto) => {
        this.vee = data;

        this.veeForm.get('id')?.setValue(this.vee.id);
        this.veeForm.get('name')?.setValue(this.vee.name);
        this.veeForm.get('estimateAfter')?.setValue(this.vee.estimateAfter);
        this.veeForm.get('estimationMethods')?.setValue(this.vee.estimationMethods);

        // Sort the elements
        data.estimationMethods?.sort((a, b) => (a.sortorder ?? 0) - (b.sortorder ?? 0));

        data.estimationMethods?.forEach((element: EstimationGroupMethodDto) => {
          if (element.method) {
            this.activateByMethod(element.method, element.estimationMethod)
          }
        })
      });
    }
  }

  public activateEstimationMethod(method: VeeCreateObject): void {
    if (this.availableEstimationMethods.some(m => m.method === method.method)) {
      this.activeEstimationMethods.push(method);
      this.availableEstimationMethods = this.availableEstimationMethods.filter(m => m.method !== method.method);
    }
  }

  public deactivateEstimationMethod(method: VeeCreateObject): void {
    if (this.activeEstimationMethods.some(m => m.method === method.method)) {
      this.availableEstimationMethods.push(method);
      this.activeEstimationMethods = this.activeEstimationMethods.filter(m => m.method !== method.method);
    }
  }

  public activateByMethod(method: string | number, estMethod?: EstimationGroupMethodMethod): void {
    const m = this.availableEstimationMethods.find(m => m.method === this.resolveEnumFromMethod(String(method)))

    if (m) {
      // "AsRead" -> true (checked)
      // "Estimated" or "NotSpecified" -> false (unchecked)
      m.estimationMethod = estMethod === EstimationGroupMethodMethod.AsRead;
      this.activateEstimationMethod(m);
    }
  }

  public dropList(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  public resolveEnumFromMethod(method: string): number {
    return EstimationMethod[method as keyof typeof EstimationMethod];
  }

  public submit(): void {
    this.structureEstimationMethods();

    if (this.id) {
      this.formApiService.submit<EstimationGroupDto>(
        this.veeForm,
        (data: any) => this.veeService.veeUpdateEstimationGroupPut(data), {
          successMessage: 'vee.create.updated',
          closeModal: true,
        }
      )
    } else {
      this.formApiService.submit<EstimationGroupDto>(
        this.veeForm,
        (data: any) => this.veeService.veeNewEstimationGroupPost(data), {
          successMessage: 'vee.create.created',
          closeModal: true,
        }
      )
    }
  }

  public structureEstimationMethods(): void {
    this.veeForm.get('estimationMethods')?.setValue(this.activeEstimationMethods.map(method => {
      let estimationMethodValue: string;
      if (method.method === EstimationMethod.GhostMeter) {
        estimationMethodValue = EstimationGroupMethodMethod.AsRead;
      } else {
        estimationMethodValue = method.estimationMethod ? EstimationGroupMethodMethod.AsRead : EstimationGroupMethodMethod.Estimated;
      }

      return {
        method: method.method,
        sortorder: this.activeEstimationMethods.indexOf(method) + 1,
        estimationMethod: estimationMethodValue
      }
    }));
  }
}
